import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ControlBadge } from "@/components/ControlBadge";
import { MediaPicker } from "@/components/cms/MediaPicker";
import { RichText, RichTextView, plainToHtml } from "@/components/cms/RichText";
import {
  Loader2, ArrowLeft, Save, Send, CheckCircle2, XCircle, Archive, Plus, Trash2, FileEdit, Globe, ShieldCheck,
  Circle, AlertTriangle, ListChecks, ExternalLink, Download,
} from "lucide-react";

const ic = "text-[14px] rounded-lg border-[#1C4358]/20";
const STATUS = {
  draft: { c: "#475569", bg: "rgba(100,116,139,.14)", label: "Draft" },
  pending_review: { c: "#B7791F", bg: "rgba(183,121,31,.14)", label: "Pending review" },
  approved: { c: "#2F7D5A", bg: "rgba(47,125,90,.14)", label: "Approved" },
  rejected: { c: "#9C1E22", bg: "rgba(156,30,34,.12)", label: "Needs changes" },
  archived: { c: "#7A8A99", bg: "rgba(122,138,153,.14)", label: "Archived" },
};

const StatusChip = ({ s, live }) => {
  const st = STATUS[s] || STATUS.draft;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ color: st.c, backgroundColor: st.bg }} data-testid={`ov-status-${s}`}>{st.label}</span>
      {live && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#2F7D5A]/15 text-[#2F7D5A]">● Live</span>}
    </span>
  );
};

const emptyRow = {
  faqs: { q: "", a: "" }, links: { label: "", url: "" },
  team: { name: "", role: "", bio: "", photo_url: "" },
  partners: { org_name: "", type: "", blurb: "", category: "" },
};

function ValueEditor({ type, value, onChange, disabled }) {
  if (type === "text") return <Input className={ic} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="ov-input" />;
  if (type === "textarea") return <Textarea rows={3} className={ic} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="ov-input" />;
  // list types
  const arr = Array.isArray(value) ? value : [];
  const cols = Object.keys(emptyRow[type] || {});
  const upd = (i, k, v) => onChange(arr.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const richKey = type === "team" ? "bio" : type === "partners" ? "blurb" : null;
  return (
    <div className="space-y-2" data-testid="ov-list">
      {arr.map((row, i) => (
        <div key={i} className="rounded-lg border border-[#1C4358]/12 p-3 space-y-2 bg-[#F9FBFC]">
          {cols.map((k) => {
            if (k === "photo_url") {
              return disabled
                ? (row.photo_url ? <img key={k} src={row.photo_url} alt={row.photo_alt || ""} className="h-16 w-16 rounded-full object-cover border border-[#1C4358]/15" /> : null)
                : <MediaPicker key={k} label="Photo" testid={`ov-photo-${i}`} value={row.photo_url || ""} alt={row.photo_alt || ""} onUrlChange={(u) => upd(i, "photo_url", u)} onAltChange={(a) => upd(i, "photo_alt", a)} />;
            }
            if (k === richKey) {
              const html = row[`${k}_html`] ?? plainToHtml(row[k]);
              const rtLabel = k === "bio" ? "Short bio" : "Blurb";
              return disabled
                ? (html ? <RichTextView key={k} html={html} className="rounded-lg border border-[#E6EDF2] bg-white p-2 text-[13px]" /> : null)
                : (
                  <div key={k} className="space-y-1">
                    <span className="text-[11px] font-semibold text-[#7A8A99]">{rtLabel}</span>
                    <RichText testid={`ov-rt-${k}-${i}`} value={html} placeholder={`${rtLabel}…`} onChange={(h) => upd(i, `${k}_html`, h)} />
                  </div>
                );
            }
            return k === "a"
              ? <Textarea key={k} rows={2} disabled={disabled} className={ic} placeholder={k} value={row[k] || ""} onChange={(e) => upd(i, k, e.target.value)} />
              : <Input key={k} disabled={disabled} className={ic} placeholder={k} value={row[k] || ""} onChange={(e) => upd(i, k, e.target.value)} />;
          })}
          {!disabled && <button onClick={() => onChange(arr.filter((_, j) => j !== i))} className="text-[12px] text-[#9C1E22] inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>}
        </div>
      ))}
      {!disabled && <button onClick={() => onChange([...arr, { ...emptyRow[type] }])} className="text-[13px] text-[#1C4358] inline-flex items-center gap-1 font-medium" data-testid="ov-add-row"><Plus size={14} /> Add</button>}
    </div>
  );
}

function FieldCard({ f, caps, onSaved }) {
  const key = f.service_lane ? `${f.field}::${f.service_lane}` : f.field;
  const o = f.override;
  const [value, setValue] = useState(o?.proposed_local_value ?? (["faqs", "links", "team", "partners"].includes(f.type) ? [] : ""));
  const [busy, setBusy] = useState(false);
  const status = o?.status;
  const canEdit = caps.can_submit && !["pending_review"].includes(status);
  const params = f.service_lane ? { service_lane: f.service_lane } : {};

  const act = async (fn) => { setBusy(true); try { await fn(); await onSaved(); } catch (e) { toast.error(errMsg(e, "Action failed.")); } finally { setBusy(false); } };
  const saveDraft = () => act(async () => { await api.put(`/franchise-admin/franchises/${f.fid}/content/${f.field}`, { value, ...params }); toast.success("Draft saved."); });
  const submit = () => act(async () => { await api.put(`/franchise-admin/franchises/${f.fid}/content/${f.field}`, { value, ...params }); await api.post(`/franchise-admin/franchises/${f.fid}/content/${f.field}/submit`, params); toast.success("Submitted for review."); });
  const approve = () => act(async () => { await api.post(`/franchise-admin/overrides/${o.id}/approve`, {}); toast.success("Approved & published."); });
  const reject = () => act(async () => { const r = window.prompt("Reason for changes?"); if (r === null) return; await api.post(`/franchise-admin/overrides/${o.id}/reject`, { reason: r }); toast.success("Sent back."); });
  const archive = () => act(async () => { await api.post(`/franchise-admin/overrides/${o.id}/archive`); toast.success("Archived."); });

  return (
    <Card className="p-4 border-[#E6EDF2] space-y-2.5" data-testid={`content-field-${key}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-display font-semibold text-[#1C4358] text-[15px]">{f.label}{f.service_lane ? ` · ${f.service_lane}` : ""}</span>
        {o ? <StatusChip s={status} live={o.is_live} /> : <ControlBadge kind="hybrid" />}
      </div>
      {f.global_default && <p className="text-[12px] text-[#7A8A99]"><Globe size={11} className="inline mr-1" /> Global default: <span className="italic">{String(f.global_default).slice(0, 120)}</span></p>}
      {o?.is_live && <p className="text-[12px] text-[#2F7D5A]">Currently live: <span className="font-medium">{typeof o.approved_value === "string" ? o.approved_value.slice(0, 120) : `${(o.approved_value || []).length} item(s)`}</span></p>}
      {status === "rejected" && o?.rejection_reason && <p className="text-[12px] text-[#9C1E22]">Reviewer note: {o.rejection_reason}</p>}
      <ValueEditor type={f.type} value={value} onChange={setValue} disabled={!canEdit} />
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {caps.can_submit && (
          <>
            <Button size="sm" variant="outline" disabled={busy || !canEdit} onClick={saveDraft} data-testid={`ov-save-${key}`} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Save size={13} /> Save draft</Button>
            <Button size="sm" disabled={busy || !canEdit} onClick={submit} data-testid={`ov-submit-${key}`} className="gap-1 bg-[#1C4358] hover:bg-[#163646] text-white"><Send size={13} /> Submit for review</Button>
          </>
        )}
        {caps.can_approve && status === "pending_review" && (
          <>
            <Button size="sm" disabled={busy} onClick={approve} data-testid={`ov-approve-${key}`} className="gap-1 bg-[#2F7D5A] hover:bg-[#255346] text-white"><CheckCircle2 size={13} /> Approve</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={reject} data-testid={`ov-reject-${key}`} className="gap-1 border-[#D6E0E7] text-[#9C1E22]"><XCircle size={13} /> Request changes</Button>
          </>
        )}
        {o && status !== "archived" && (caps.can_approve || o.submitted_by) && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={archive} data-testid={`ov-archive-${key}`} className="gap-1 text-[#7A8A99]"><Archive size={13} /> Archive</Button>
        )}
      </div>
    </Card>
  );
}

const STAT_ICON = {
  ok: { I: CheckCircle2, c: "#2F7D5A" },
  warn: { I: AlertTriangle, c: "#B7791F" },
  todo: { I: Circle, c: "#9AA8B2" },
};

function CheckRow({ item }) {
  const s = STAT_ICON[item.status] || STAT_ICON.todo;
  const I = s.I;
  return (
    <div className="flex items-start gap-2.5 py-1.5" data-testid={`seo-check-${item.key}`}>
      <I size={16} style={{ color: s.c }} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[13.5px] font-medium text-[#1C4358]">{item.label}</p>
        {item.detail && <p className="text-[12px] text-[#7A8A99] break-words">{item.detail}</p>}
      </div>
    </div>
  );
}

function SeoChecklist({ fid }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    api.get(`/franchise-admin/franchises/${fid}/seo-checklist`).then((r) => setD(r.data)).catch(() => {});
  }, [fid]);
  if (!d) return null;
  const r = d.readiness;
  return (
    <Card className="p-4 border-[#E6EDF2] mb-6" data-testid="seo-checklist-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-2" data-testid="seo-checklist-toggle">
          <span className="inline-flex items-center gap-2 font-display font-semibold text-[#1C4358] text-[15px]">
            <ListChecks size={17} /> Local SEO &amp; schema readiness
          </span>
          <span
            className="text-[12px] font-bold px-2 py-0.5 rounded"
            style={{ color: r.ready ? "#2F7D5A" : "#B7791F", backgroundColor: r.ready ? "rgba(47,125,90,.14)" : "rgba(183,121,31,.14)" }}
            data-testid="seo-readiness-score"
          >
            {r.ok}/{r.total} setup complete
          </span>
        </button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/franchise-admin/${fid}/local-profile-export`)}
          data-testid="lpe-open-btn"
          className="gap-1.5 border-[#D6E0E7] text-[#1C4358]"
        >
          <Download size={14} /> Export Local Profile Info
        </Button>
      </div>
      {open && (
        <div className="mt-3 grid gap-5 sm:grid-cols-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A8A99] mb-1">Content &amp; SEO</p>
            {d.checklist.map((i) => <CheckRow key={i.key} item={i} />)}
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A8A99] mb-1">Schema markup</p>
            {d.schema.map((i) => <CheckRow key={i.key} item={i} />)}
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A8A99] mb-1">Duplicate-page prevention</p>
            {d.duplicate_prevention.map((i) => <CheckRow key={i.key} item={i} />)}
            {d.franchise.local_path && (
              <a
                href={d.franchise.local_path}
                target="_blank"
                rel="noreferrer"
                data-testid="seo-view-public"
                className="mt-2 inline-flex items-center gap-1 text-[12.5px] text-[#1C4358] underline hover:text-[#9C1E22]"
              >
                <ExternalLink size={12} /> View public page
              </a>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function FranchiseContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/franchise-admin/franchises/${id}/content`);
      data.fields.forEach((f) => (f.fid = id));
      data.lane_fields.forEach((f) => (f.fid = id));
      setData(data);
    } catch (e) { toast.error(errMsg(e, "Could not load content.")); if (e?.response?.status === 403) navigate("/franchise-admin"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  const caps = data.capabilities;

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="franchise-content-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate(`/franchise-admin/${id}`)} data-testid="content-back-btn" className="text-[#1C4358] hover:text-[#9C1E22]"><ArrowLeft size={20} /></button>
          <FileEdit size={18} className="text-[#1C4358]" />
          <h1 className="text-lg font-display font-bold text-[#1C4358] truncate">{data.franchise.name} — Local Content</h1>
        </div>
        {caps.can_approve && (
          <Button variant="outline" onClick={() => navigate("/franchise-admin/overrides/review")} data-testid="go-review-queue" className="gap-1.5 border-[#D6E0E7] text-[#1C4358]">
            <ShieldCheck size={15} /> Review queue
          </Button>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 text-[13px] text-[#4B5563]">
        <ControlBadge kind="hybrid" /> Hybrid content — local drafts, Super Admin approves before it goes public.
      </div>

      <div className="mt-6">
        <SeoChecklist fid={id} />
      </div>

      <div className="mt-6 space-y-8">
        {data.groups.map((g) => {
          const base = data.fields.filter((f) => f.group === g.key);
          const lanes = data.lane_fields.filter((f) => f.group === g.key);
          if (base.length === 0 && lanes.length === 0) return null;
          return (
            <div key={g.key} data-testid={`content-group-${g.key}`}>
              <h2 className="text-[15px] font-display font-semibold text-[#1C4358] mb-3">{g.title}</h2>
              <div className="space-y-3">
                {base.map((f) => <FieldCard key={f.field} f={f} caps={caps} onSaved={load} />)}
                {lanes.map((f) => <FieldCard key={`${f.field}::${f.service_lane}`} f={f} caps={caps} onSaved={load} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
